/**
 * Sim Lab stage library + session map override (battle catalog untouched).
 */
import stagesDoc from "../../../data/tables/raid/sim-stages.json";
import { raidCatalog } from "../../../data/raid-catalog";
import type { SimPlayBounds, SimStageDef } from "./sim-lab-types";
import { insetPlayBounds } from "./sim-lab-view";

const stages = stagesDoc.stages as SimStageDef[];

export type SimMapOverride = {
  width: number;
  height: number;
  playBounds: SimPlayBounds;
};

let mapOverride: SimMapOverride | null = null;

export function listSimStages(): readonly SimStageDef[] {
  return stages;
}

export function getSimStage(id: string): SimStageDef {
  const row = stages.find((s) => s.id === id);
  if (!row) throw new Error(`unknown sim stage: ${id}`);
  return row;
}

/**
 * Synthetic stage matching battle catalog play map (no Sim Lab pad override).
 * Anchors follow catalog spawn / boss seats so assault formation stays play-facing.
 */
export function buildPlayMapStage(): SimStageDef {
  const map = raidCatalog.balance.map as {
    width: number;
    height: number;
    allySpawnY: number;
    bossX: number;
    bossY: number;
    playBounds?: SimPlayBounds;
  };
  const playBounds: SimPlayBounds = map.playBounds ?? {
    xMin: 56,
    xMax: map.width - 56,
    yMin: 130,
    yMax: map.height - 100,
  };
  const midX = (playBounds.xMin + playBounds.xMax) * 0.5;
  return {
    id: "play_map",
    labelKey: "raid.sim.stage.play_map",
    width: Number(map.width) || 540,
    height: Number(map.height) || 960,
    playBounds: { ...playBounds },
    view: { fit: "playBounds", padFrac: 0.02 },
    anchors: {
      focus: { x: midX, y: Number(map.allySpawnY) || 620 },
      boss: {
        x: Number(map.bossX) || midX,
        y: Number(map.bossY) || 220,
      },
      ally: { x: midX, y: Number(map.allySpawnY) || 620 },
    },
    ground: { mode: "flat", color: "#12141a" },
  };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Apply stage.uiSafeInset so playBounds/anchors clear Sim Lab chrome.
 * Camera framing still zooms the visible gap; this keeps AI goals inside it.
 */
export function resolveSimStageForRun(stage: SimStageDef): SimStageDef {
  const inset = stage.uiSafeInset;
  if (!inset) return stage;
  const playBounds = insetPlayBounds(stage.playBounds, inset);
  const pad = 24;
  const anchors = { ...stage.anchors };
  if (anchors.focus) {
    anchors.focus = {
      x: clamp(anchors.focus.x, playBounds.xMin + pad, playBounds.xMax - pad),
      y: clamp(anchors.focus.y, playBounds.yMin + pad, playBounds.yMax - pad),
    };
  }
  if (anchors.boss) {
    anchors.boss = {
      x: clamp(anchors.boss.x, playBounds.xMin + pad, playBounds.xMax - pad),
      y: clamp(anchors.boss.y, playBounds.yMin + pad, playBounds.yMax - pad),
    };
  }
  if (anchors.ally) {
    anchors.ally = {
      x: clamp(anchors.ally.x, playBounds.xMin + pad, playBounds.xMax - pad),
      y: clamp(anchors.ally.y, playBounds.yMin + pad, playBounds.yMax - pad),
    };
  }
  return { ...stage, playBounds, anchors };
}

export function setSimMapOverride(stage: SimStageDef): void {
  mapOverride = {
    width: stage.width,
    height: stage.height,
    playBounds: { ...stage.playBounds },
  };
}

export function clearSimMapOverride(): void {
  mapOverride = null;
}

export function getSimMapOverride(): SimMapOverride | null {
  return mapOverride;
}

/** Active playBounds: Sim Lab override or battle catalog. */
export function getActivePlayBounds(): SimPlayBounds {
  if (mapOverride) return mapOverride.playBounds;
  const map = raidCatalog.balance.map as {
    width: number;
    height: number;
    playBounds?: SimPlayBounds;
  };
  return (
    map.playBounds ?? {
      xMin: 56,
      xMax: map.width - 56,
      yMin: 130,
      yMax: map.height - 100,
    }
  );
}

export function getActiveMapSize(): { width: number; height: number } {
  if (mapOverride) {
    return { width: mapOverride.width, height: mapOverride.height };
  }
  const map = raidCatalog.balance.map as { width: number; height: number };
  return {
    width: Number(map.width) || 540,
    height: Number(map.height) || 960,
  };
}
